import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getSharedProjects } from '../api'

/**
 * Normalize a shared project from the API shape to the shape
 * that workspace components (ProjectGridCard, ProjectListRow, etc.) expect.
 */
function normalizeSharedProject(p) {
  const isPaid = p.order?.paid ?? p.paid ?? false
  const planName = p.order?.plan || p.plan || (isPaid ? 'Pro' : 'Free')

  return {
    ...p,
    // Components read `publishedUrl`; the shared API returns `publishedLink`
    publishedUrl: p.publishedUrl || p.publishedLink || '',
    // Components read `order.paid` / `order.plan`; shared API has top-level `paid`
    order: {
      ...(p.order || {}),
      paid: isPaid,
      plan: planName,
    },
  }
}

export function useSharedProjects({ page = 1, search = '' } = {}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['sharedProjects', page, search],
    queryFn: () => getSharedProjects({ page, search }),
    staleTime: 5 * 60 * 1000,
  })

  const projects = useMemo(() => {
    const raw = data?.data || []
    return raw.map(normalizeSharedProject)
  }, [data])

  return {
    projects,
    pagination: data?.pagination || {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 100,
      hasNextPage: false,
      hasPreviousPage: false,
    },
    loading: isLoading,
    error: error?.message || null,
  }
}

